import classNames from 'classnames';
import omit from 'lodash.omit';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';
import {connect} from 'react-redux';
import MediaQuery from 'react-responsive';
import {Tab, Tabs, TabList, TabPanel} from 'react-tabs';
import tabStyles from 'react-tabs/style/react-tabs.css';
import VM from 'scratch-vm';
import Renderer from 'scratch-render';

import Blocks from '../../containers/blocks.jsx';
import CostumeTab from '../../containers/costume-tab.jsx';
import TargetPane from '../../containers/target-pane.jsx';
import SoundTab from '../../containers/sound-tab.jsx';
import StageWrapper from '../../containers/stage-wrapper.jsx';
import Loader from '../loader/loader.jsx';
import Box from '../box/box.jsx';
import MenuBar from '../menu-bar/menu-bar.jsx';
import CostumeLibrary from '../../containers/costume-library.jsx';
import BackdropLibrary from '../../containers/backdrop-library.jsx';
import Watermark from '../../containers/watermark.jsx';
import WebGlModal from '../../containers/webgl-modal.jsx';
import TipsLibrary from '../../containers/tips-library.jsx';
import Cards from '../../containers/cards.jsx';
import Alerts from '../../containers/alerts.jsx';
import DragLayer from '../../containers/drag-layer.jsx';
import ConnectionModal from '../../containers/connection-modal.jsx';
import TelemetryModal from '../telemetry-modal/telemetry-modal.jsx';
import FloatingPanel from './floating-panel.jsx';
import AISidebar from '../ai-sidebar/ai-sidebar.jsx';

import layout, {STAGE_DISPLAY_SIZES, STAGE_SIZE_MODES} from '../../lib/layout-constants';
import {getStageDimensions, resolveStageSize} from '../../lib/screen-utils';
import {themeMap} from '../../lib/themes';
import {setFloatingMode} from '../../reducers/mode';
import {closeAISidebar} from '../../reducers/modals';
import {moveFloatingPanel, resizeFloatingPanel} from '../../reducers/floating-panels';

import styles from './gui.css';
import addExtensionIcon from './icon--extensions.svg';
import codeIcon from './icon--code.svg';
import costumesIcon from './icon--costumes.svg';
import soundsIcon from './icon--sounds.svg';
import DebugModal from '../debug-modal/debug-modal.jsx';

const messages = defineMessages({
    addExtension: {
        id: 'gui.gui.addExtension',
        description: 'Button to add an extension in the target pane',
        defaultMessage: 'Add Extension'
    }
});

// Cache this value to only retrieve it once the first time.
// Assume that it doesn't change for a session.
let isRendererSupported = null;

const GUIComponent = props => {
    const bodyWrapperRef = React.useRef(null);
    const editorWrapperRef = React.useRef(null);
    const stageAndTargetWrapperRef = React.useRef(null);
    const targetWrapperRef = React.useRef(null);
    const [isFloatingTransitioning, setIsFloatingTransitioning] = React.useState(false);

    const {
        accountNavOpen,
        activeTabIndex,
        alertsVisible,
        authorId,
        authorThumbnailUrl,
        authorUsername,
        basePath,
        backdropLibraryVisible,
        blocksId,
        blocksTabVisible,
        cardsVisible,
        canChangeLanguage,
        canChangeTheme,
        canCreateNew,
        canEditTitle,
        canManageFiles,
        canRemix,
        canSave,
        canCreateCopy,
        canShare,
        canUseCloud,
        children,
        connectionModalVisible,
        costumeLibraryVisible,
        costumesTabVisible,
        debugModalVisible,
        enableCommunity,
        intl,
        isCreating,
        isFullScreen,
        isFloatingMode,
        isPlayerOnly,
        isRtl,
        isShared,
        isTelemetryEnabled,
        isTotallyNormal,
        loading,
        logo,
        renderLogin,
        aiSidebarVisible,
        onClickAbout,
        onClickAccountNav,
        onCloseAccountNav,
        onLogOut,
        onOpenRegistration,
        onToggleLoginOpen,
        onActivateCostumesTab,
        onActivateSoundsTab,
        onActivateTab,
        onChangeFloatingMode,
        onMoveFloatingPanel,
        onClickLogo,
        onExtensionButtonClick,
        onProjectTelemetryEvent,
        onRequestCloseBackdropLibrary,
        onRequestCloseCostumeLibrary,
        onRequestCloseAISidebar,
        onRequestCloseDebugModal,
        onRequestCloseTelemetryModal,
        onSeeCommunity,
        onShare,
        onShowPrivacyPolicy,
        onStartSelectingFileUpload,
        onTelemetryModalCancel,
        onTelemetryModalOptIn,
        onTelemetryModalOptOut,
        showComingSoon,
        soundsTabVisible,
        stageSizeMode,
        floatingPanels,
        targetIsStage,
        telemetryModalVisible,
        theme,
        tipsLibraryVisible,
        vm,
        ...componentProps
    } = omit(props, 'dispatch');
    if (children) {
        return <Box {...componentProps}>{children}</Box>;
    }

    // When entering floating mode, place panels where the non-floating layout currently is
    // so the UI doesn't jump to hard-coded defaults.
    const handleChangeFloatingMode = nextIsFloatingMode => {
        if (nextIsFloatingMode && !isFloatingMode) {
            setIsFloatingTransitioning(true);
            const bodyEl = bodyWrapperRef.current;
            const codeEl = editorWrapperRef.current;
            const stageEl = stageAndTargetWrapperRef.current;
            const spritesEl = targetWrapperRef.current;

            if (bodyEl && codeEl && stageEl && spritesEl) {
                const bodyRect = bodyEl.getBoundingClientRect();
                const toRelativePos = rect => ({
                    x: rect.left - bodyRect.left,
                    y: rect.top - bodyRect.top
                });
                const clampPanelSize = (width, height, minWidth, minHeight) => ({
                    width: Math.max(minWidth, Math.round(width)),
                    height: Math.max(minHeight, Math.round(height))
                });
                const stageRect = stageEl.getBoundingClientRect();
                const spritesRect = spritesEl.getBoundingClientRect();
                const codeRect = codeEl.getBoundingClientRect();
                const clampedStageSize = clampPanelSize(stageRect.width, stageRect.height, 320, 260);
                const clampedSpritesSize = clampPanelSize(spritesRect.width, spritesRect.height, 360, 320);
                const clampedCodeSize = clampPanelSize(codeRect.width, codeRect.height, 420, 320);

                const stagePos = toRelativePos(stageRect);
                const spritesPos = toRelativePos(spritesRect);
                const codePos = toRelativePos(codeRect);

                onMoveFloatingPanel('stage', stagePos.x, stagePos.y);
                onResizeFloatingPanel('stage', clampedStageSize.width, clampedStageSize.height);
                onMoveFloatingPanel('sprites', spritesPos.x, spritesPos.y);
                onResizeFloatingPanel('sprites', clampedSpritesSize.width, clampedSpritesSize.height);
                onMoveFloatingPanel('code', codePos.x, codePos.y);
                onResizeFloatingPanel('code', clampedCodeSize.width, clampedCodeSize.height);
            }

            if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
                window.requestAnimationFrame(() => {
                    onChangeFloatingMode(true);
                    window.requestAnimationFrame(() => setIsFloatingTransitioning(false));
                });
            } else {
                onChangeFloatingMode(true);
                setIsFloatingTransitioning(false);
            }
            return;
        }

        setIsFloatingTransitioning(true);
        onChangeFloatingMode(nextIsFloatingMode);
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => setIsFloatingTransitioning(false));
        } else {
            setIsFloatingTransitioning(false);
        }
    };

    const tabClassNames = {
        tabs: styles.tabs,
        tab: classNames(tabStyles.reactTabsTab, styles.tab),
        tabList: classNames(tabStyles.reactTabsTabList, styles.tabList),
        tabPanel: classNames(tabStyles.reactTabsTabPanel, styles.tabPanel),
        tabPanelSelected: classNames(tabStyles.reactTabsTabPanelSelected, styles.isSelected),
        tabSelected: classNames(tabStyles.reactTabsTabSelected, styles.isSelected)
    };

    if (isRendererSupported === null) {
        isRendererSupported = Renderer.isSupported();
    }

    return (<MediaQuery minWidth={layout.fullSizeMinWidth}>{isFullSize => {
        const stageSize = resolveStageSize(stageSizeMode, isFullSize);
        const largeStageDimensions = getStageDimensions(STAGE_DISPLAY_SIZES.large, false);
        const smallStageDimensions = getStageDimensions(STAGE_DISPLAY_SIZES.small, false);
        const stageMenuHeight = 44;
        const getFloatingStageSize = panelSize => {
            const normalFits = panelSize.width >= largeStageDimensions.width &&
                panelSize.height >= (largeStageDimensions.height + stageMenuHeight);
            if (normalFits) {
                return STAGE_DISPLAY_SIZES.large;
            }

            const halfFits = panelSize.width >= smallStageDimensions.width &&
                panelSize.height >= (smallStageDimensions.height + stageMenuHeight);
            if (halfFits) {
                return STAGE_DISPLAY_SIZES.small;
            }

            return STAGE_DISPLAY_SIZES.small;
        };

        const floatingStageSize = isFullScreen
            ? STAGE_DISPLAY_SIZES.large
            : getFloatingStageSize(floatingPanels.stage);

        const editorArea = (
                        <Box className={classNames(styles.editorWrapper, styles.floatingEditorWrapper)}>
                            <Tabs
                                forceRenderTabPanel
                                className={tabClassNames.tabs}
                                selectedIndex={activeTabIndex}
                                selectedTabClassName={tabClassNames.tabSelected}
                                selectedTabPanelClassName={tabClassNames.tabPanelSelected}
                                onSelect={onActivateTab}
                            >
                                <TabList className={tabClassNames.tabList}>
                                    <Tab className={tabClassNames.tab}>
                                        <img
                                            draggable={false}
                                            src={codeIcon}
                                        />
                                        <FormattedMessage
                                            defaultMessage="Code"
                                            description="Button to get to the code panel"
                                            id="gui.gui.codeTab"
                                        />
                                    </Tab>
                                    <Tab
                                        className={tabClassNames.tab}
                                        onClick={onActivateCostumesTab}
                                    >
                                        <img
                                            draggable={false}
                                            src={costumesIcon}
                                        />
                                        {targetIsStage ? (
                                            <FormattedMessage
                                                defaultMessage="Backdrops"
                                                description="Button to get to the backdrops panel"
                                                id="gui.gui.backdropsTab"
                                            />
                                        ) : (
                                            <FormattedMessage
                                                defaultMessage="Costumes"
                                                description="Button to get to the costumes panel"
                                                id="gui.gui.costumesTab"
                                            />
                                        )}
                                    </Tab>
                                    <Tab
                                        className={tabClassNames.tab}
                                        onClick={onActivateSoundsTab}
                                    >
                                        <img
                                            draggable={false}
                                            src={soundsIcon}
                                        />
                                        <FormattedMessage
                                            defaultMessage="Sounds"
                                            description="Button to get to the sounds panel"
                                            id="gui.gui.soundsTab"
                                        />
                                    </Tab>
                                </TabList>
                                <TabPanel className={tabClassNames.tabPanel}>
                                    <Box className={styles.blocksWrapper}>
                                        <Blocks
                                            key={`${blocksId}/${theme}`}
                                            canUseCloud={canUseCloud}
                                            floatingWorkspaceSize={isFloatingMode ? floatingPanels.code : null}
                                            floatingWorkspacePosition={isFloatingMode ? floatingPanels.code : null}
                                            grow={1}
                                            isVisible={blocksTabVisible}
                                            options={{
                                                media: `${basePath}static/${themeMap[theme].blocksMediaFolder}/`
                                            }}
                                            stageSize={stageSize}
                                            theme={theme}
                                            vm={vm}
                                        />
                                    </Box>
                                    <Box className={styles.extensionButtonContainer}>
                                        <button
                                            className={styles.extensionButton}
                                            title={intl.formatMessage(messages.addExtension)}
                                            onClick={onExtensionButtonClick}
                                        >
                                            <img
                                                className={styles.extensionButtonIcon}
                                                draggable={false}
                                                src={addExtensionIcon}
                                            />
                                        </button>
                                    </Box>
                                    <Box className={styles.watermark}>
                                        <Watermark />
                                    </Box>
                                </TabPanel>
                                <TabPanel className={tabClassNames.tabPanel}>
                                    {costumesTabVisible ? <CostumeTab vm={vm} /> : null}
                                </TabPanel>
                                <TabPanel className={tabClassNames.tabPanel}>
                                    {soundsTabVisible ? <SoundTab vm={vm} /> : null}
                                </TabPanel>
                            </Tabs>
                        </Box>

        );

        const stageArea = (
            <StageWrapper
                isFullScreen={isFullScreen}
                isRendererSupported={isRendererSupported}
                isRtl={isRtl}
                loading={loading}
                stageSize={isFloatingMode ? floatingStageSize : stageSize}
                vm={vm}
            />
        );

        const targetArea = (
            <Box className={styles.targetWrapper}>
                <TargetPane
                    stageSize={isFloatingMode ? STAGE_DISPLAY_SIZES.large : stageSize}
                    vm={vm}
                />
            </Box>
        );

        const floatingWorkspace = isFloatingMode ? (
            <Box
                className={styles.floatingWorkspace}
                style={{
                    opacity: isFloatingTransitioning ? 0 : 1,
                    pointerEvents: isFloatingTransitioning ? 'none' : 'auto',
                    transition: 'opacity 120ms ease'
                }}
            >
                <FloatingPanel
                    defaultPosition={floatingPanels.stage}
                    defaultSize={{width: floatingPanels.stage.width, height: floatingPanels.stage.height}}
                    headerLabel={(
                        <FormattedMessage
                            defaultMessage="Stage"
                            description="Draggable stage panel"
                            id="gui.gui.stagePanel"
                        />
                    )}
                    onStop={(e_, data) => onMoveFloatingPanel('stage', data.x, data.y)}
                    onResize={(width, height) => onResizeFloatingPanel('stage', width, height)}
                    onResizeStop={(width, height) => onResizeFloatingPanel('stage', width, height)}
                    minSize={{width: 320, height: 260}}
                    style={{zIndex: 2}}
                >
                    <Box className={styles.stageFloatingBody}>
                        <Box className={styles.stageFloatingStage}>
                            {stageArea}
                        </Box>
                    </Box>
                </FloatingPanel>
                <FloatingPanel
                    defaultPosition={floatingPanels.sprites}
                    defaultSize={{width: floatingPanels.sprites.width, height: floatingPanels.sprites.height}}
                    headerLabel={(
                        <FormattedMessage
                            defaultMessage="Sprites"
                            description="Draggable sprite info panel"
                            id="gui.gui.spritesPanel"
                        />
                    )}
                    onStop={(e_, data) => onMoveFloatingPanel('sprites', data.x, data.y)}
                    onResize={(width, height) => onResizeFloatingPanel('sprites', width, height)}
                    onResizeStop={(width, height) => onResizeFloatingPanel('sprites', width, height)}
                    minSize={{width: 360, height: 320}}
                    style={{zIndex: 1}}
                >
                    {targetArea}
                </FloatingPanel>
                <FloatingPanel
                    bodyClassName={styles.floatingEditorBody}
                    defaultPosition={floatingPanels.code}
                    defaultSize={{width: floatingPanels.code.width, height: floatingPanels.code.height}}
                    headerLabel={(
                        <FormattedMessage
                            defaultMessage="Code"
                            description="Draggable code blocks panel"
                            id="gui.gui.codePanel"
                        />
                    )}
                    onStop={(e_, data) => onMoveFloatingPanel('code', data.x, data.y)}
                    onResize={(width, height) => onResizeFloatingPanel('code', width, height)}
                    onResizeStop={(width, height) => onResizeFloatingPanel('code', width, height)}
                    minSize={{width: 420, height: 320}}
                    style={{
                        zIndex: 3
                    }}
                >
                    {editorArea}
                </FloatingPanel>
            </Box>
        ) : null;

        return isPlayerOnly ? (
            <StageWrapper
                isFullScreen={isFullScreen}
                isRendererSupported={isRendererSupported}
                isRtl={isRtl}
                loading={loading}
                stageSize={STAGE_SIZE_MODES.large}
                vm={vm}
            >
                {alertsVisible ? (
                    <Alerts className={styles.alertsContainer} />
                ) : null}
            </StageWrapper>
        ) : (
            <Box
                className={styles.pageWrapper}
                dir={isRtl ? 'rtl' : 'ltr'}
                {...componentProps}
            >
                {telemetryModalVisible ? (
                    <TelemetryModal
                        isRtl={isRtl}
                        isTelemetryEnabled={isTelemetryEnabled}
                        onCancel={onTelemetryModalCancel}
                        onOptIn={onTelemetryModalOptIn}
                        onOptOut={onTelemetryModalOptOut}
                        onRequestClose={onRequestCloseTelemetryModal}
                        onShowPrivacyPolicy={onShowPrivacyPolicy}
                    />
                ) : null}
                {typeof document !== 'undefined' ? ReactDOM.createPortal(
                    <AISidebar
                        isOpen={aiSidebarVisible}
                        onRequestClose={onRequestCloseAISidebar}
                    />,
                    document.body
                ) : null}
                {loading ? (
                    <Loader />
                ) : null}
                {isCreating ? (
                    <Loader messageId="gui.loader.creating" />
                ) : null}
                {isRendererSupported ? null : (
                    <WebGlModal isRtl={isRtl} />
                )}
                {tipsLibraryVisible ? (
                    <TipsLibrary />
                ) : null}
                {cardsVisible ? (
                    <Cards />
                ) : null}
                {alertsVisible ? (
                    <Alerts className={styles.alertsContainer} />
                ) : null}
                {connectionModalVisible ? (
                    <ConnectionModal
                        vm={vm}
                    />
                ) : null}
                {costumeLibraryVisible ? (
                    <CostumeLibrary
                        vm={vm}
                        onRequestClose={onRequestCloseCostumeLibrary}
                    />
                ) : null}
                {<DebugModal
                    isOpen={debugModalVisible}
                    onClose={onRequestCloseDebugModal}
                />}
                {backdropLibraryVisible ? (
                    <BackdropLibrary
                        vm={vm}
                        onRequestClose={onRequestCloseBackdropLibrary}
                    />
                ) : null}
                <MenuBar
                    accountNavOpen={accountNavOpen}
                    authorId={authorId}
                    authorThumbnailUrl={authorThumbnailUrl}
                    authorUsername={authorUsername}
                    canChangeLanguage={canChangeLanguage}
                    canChangeTheme={canChangeTheme}
                    canCreateCopy={canCreateCopy}
                    canCreateNew={canCreateNew}
                    canEditTitle={canEditTitle}
                    canManageFiles={canManageFiles}
                    canRemix={canRemix}
                    canSave={canSave}
                    canShare={canShare}
                    className={styles.menuBarPosition}
                    enableCommunity={enableCommunity}
                    isFloatingMode={isFloatingMode}
                    isShared={isShared}
                    isTotallyNormal={isTotallyNormal}
                    logo={logo}
                    renderLogin={renderLogin}
                    showComingSoon={showComingSoon}
                    onClickAbout={onClickAbout}
                    onClickAccountNav={onClickAccountNav}
                    onClickLogo={onClickLogo}
                    onCloseAccountNav={onCloseAccountNav}
                    onChangeFloatingMode={handleChangeFloatingMode}
                    onLogOut={onLogOut}
                    onOpenRegistration={onOpenRegistration}
                    onProjectTelemetryEvent={onProjectTelemetryEvent}
                    onSeeCommunity={onSeeCommunity}
                    onShare={onShare}
                    onStartSelectingFileUpload={onStartSelectingFileUpload}
                    onToggleLoginOpen={onToggleLoginOpen}
                />
                <Box
                    className={styles.bodyWrapper}
                    componentRef={el => { bodyWrapperRef.current = el; }}
                >
                    {isFloatingMode ? floatingWorkspace : (
                    <Box className={styles.flexWrapper}>
                        <Box
                            className={styles.editorWrapper}
                            componentRef={el => { editorWrapperRef.current = el; }}
                        >
                            <Tabs
                                forceRenderTabPanel
                                className={tabClassNames.tabs}
                                selectedIndex={activeTabIndex}
                                selectedTabClassName={tabClassNames.tabSelected}
                                selectedTabPanelClassName={tabClassNames.tabPanelSelected}
                                onSelect={onActivateTab}
                            >
                                <TabList className={tabClassNames.tabList}>
                                    <Tab className={tabClassNames.tab}>
                                        <img
                                            draggable={false}
                                            src={codeIcon}
                                        />
                                        <FormattedMessage
                                            defaultMessage="Code"
                                            description="Button to get to the code panel"
                                            id="gui.gui.codeTab"
                                        />
                                    </Tab>
                                    <Tab
                                        className={tabClassNames.tab}
                                        onClick={onActivateCostumesTab}
                                    >
                                        <img
                                            draggable={false}
                                            src={costumesIcon}
                                        />
                                        {targetIsStage ? (
                                            <FormattedMessage
                                                defaultMessage="Backdrops"
                                                description="Button to get to the backdrops panel"
                                                id="gui.gui.backdropsTab"
                                            />
                                        ) : (
                                            <FormattedMessage
                                                defaultMessage="Costumes"
                                                description="Button to get to the costumes panel"
                                                id="gui.gui.costumesTab"
                                            />
                                        )}
                                    </Tab>
                                    <Tab
                                        className={tabClassNames.tab}
                                        onClick={onActivateSoundsTab}
                                    >
                                        <img
                                            draggable={false}
                                            src={soundsIcon}
                                        />
                                        <FormattedMessage
                                            defaultMessage="Sounds"
                                            description="Button to get to the sounds panel"
                                            id="gui.gui.soundsTab"
                                        />
                                    </Tab>
                                </TabList>
                                <TabPanel className={tabClassNames.tabPanel}>
                                    <Box className={styles.blocksWrapper}>
                                        <Blocks
                                            key={`${blocksId}/${theme}`}
                                            canUseCloud={canUseCloud}
                                            grow={1}
                                            isVisible={blocksTabVisible}
                                            options={{
                                                media: `${basePath}static/${themeMap[theme].blocksMediaFolder}/`
                                            }}
                                            stageSize={stageSize}
                                            theme={theme}
                                            vm={vm}
                                        />
                                    </Box>
                                    <Box className={styles.extensionButtonContainer}>
                                        <button
                                            className={styles.extensionButton}
                                            title={intl.formatMessage(messages.addExtension)}
                                            onClick={onExtensionButtonClick}
                                        >
                                            <img
                                                className={styles.extensionButtonIcon}
                                                draggable={false}
                                                src={addExtensionIcon}
                                            />
                                        </button>
                                    </Box>
                                    <Box className={styles.watermark}>
                                        <Watermark />
                                    </Box>
                                </TabPanel>
                                <TabPanel className={tabClassNames.tabPanel}>
                                    {costumesTabVisible ? <CostumeTab vm={vm} /> : null}
                                </TabPanel>
                                <TabPanel className={tabClassNames.tabPanel}>
                                    {soundsTabVisible ? <SoundTab vm={vm} /> : null}
                                </TabPanel>
                            </Tabs>
                        </Box>

                        <Box
                            className={classNames(styles.stageAndTargetWrapper, styles[stageSize])}
                            componentRef={el => { stageAndTargetWrapperRef.current = el; }}
                        >
                            <StageWrapper
                                isFullScreen={isFullScreen}
                                isRendererSupported={isRendererSupported}
                                isRtl={isRtl}
                                stageSize={stageSize}
                                vm={vm}
                            />
                            <Box
                                className={styles.targetWrapper}
                                componentRef={el => { targetWrapperRef.current = el; }}
                            >
                                <TargetPane
                                    stageSize={stageSize}
                                    vm={vm}
                                />
                            </Box>
                        </Box>
                    </Box>
                    )}
                </Box>
                <DragLayer />
            </Box>
        );
    }}</MediaQuery>);
};

GUIComponent.propTypes = {
    accountNavOpen: PropTypes.bool,
    activeTabIndex: PropTypes.number,
    authorId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), // can be false
    authorThumbnailUrl: PropTypes.string,
    authorUsername: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), // can be false
    backdropLibraryVisible: PropTypes.bool,
    basePath: PropTypes.string,
    blocksTabVisible: PropTypes.bool,
    blocksId: PropTypes.string,
    aiSidebarVisible: PropTypes.bool,
    canChangeLanguage: PropTypes.bool,
    canChangeTheme: PropTypes.bool,
    canCreateCopy: PropTypes.bool,
    canCreateNew: PropTypes.bool,
    canEditTitle: PropTypes.bool,
    canManageFiles: PropTypes.bool,
    canRemix: PropTypes.bool,
    canSave: PropTypes.bool,
    canShare: PropTypes.bool,
    canUseCloud: PropTypes.bool,
    cardsVisible: PropTypes.bool,
    children: PropTypes.node,
    costumeLibraryVisible: PropTypes.bool,
    costumesTabVisible: PropTypes.bool,
    debugModalVisible: PropTypes.bool,
    enableCommunity: PropTypes.bool,
    intl: intlShape.isRequired,
    isCreating: PropTypes.bool,
    isFullScreen: PropTypes.bool,
    isFloatingMode: PropTypes.bool,
    isPlayerOnly: PropTypes.bool,
    isRtl: PropTypes.bool,
    isShared: PropTypes.bool,
    isTotallyNormal: PropTypes.bool,
    loading: PropTypes.bool,
    logo: PropTypes.string,
    onActivateCostumesTab: PropTypes.func,
    onActivateSoundsTab: PropTypes.func,
    onActivateTab: PropTypes.func,
    onChangeFloatingMode: PropTypes.func,
    onClickAccountNav: PropTypes.func,
    onClickLogo: PropTypes.func,
    onCloseAccountNav: PropTypes.func,
    onExtensionButtonClick: PropTypes.func,
    onLogOut: PropTypes.func,
    onMoveFloatingPanel: PropTypes.func,
    onResizeFloatingPanel: PropTypes.func,
    onOpenRegistration: PropTypes.func,
    onRequestCloseBackdropLibrary: PropTypes.func,
    onRequestCloseAISidebar: PropTypes.func,
    onRequestCloseCostumeLibrary: PropTypes.func,
    onRequestCloseDebugModal: PropTypes.func,
    onRequestCloseTelemetryModal: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onShare: PropTypes.func,
    onShowPrivacyPolicy: PropTypes.func,
    onStartSelectingFileUpload: PropTypes.func,
    onTabSelect: PropTypes.func,
    onTelemetryModalCancel: PropTypes.func,
    onTelemetryModalOptIn: PropTypes.func,
    onTelemetryModalOptOut: PropTypes.func,
    onToggleLoginOpen: PropTypes.func,
    renderLogin: PropTypes.func,
    showComingSoon: PropTypes.bool,
    soundsTabVisible: PropTypes.bool,
    stageSizeMode: PropTypes.oneOf(Object.keys(STAGE_SIZE_MODES)),
    floatingPanels: PropTypes.objectOf(PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number,
        x: PropTypes.number,
        y: PropTypes.number
    })),
    targetIsStage: PropTypes.bool,
    telemetryModalVisible: PropTypes.bool,
    theme: PropTypes.string,
    tipsLibraryVisible: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired
};
GUIComponent.defaultProps = {
    basePath: './',
    blocksId: 'original',
    canChangeLanguage: true,
    canChangeTheme: true,
    canCreateNew: false,
    canEditTitle: false,
    canManageFiles: true,
    canRemix: false,
    canSave: false,
    canCreateCopy: false,
    canShare: false,
    canUseCloud: false,
    enableCommunity: false,
    isCreating: false,
    isFloatingMode: false,
    isShared: false,
    isTotallyNormal: false,
    loading: false,
    floatingPanels: {
        code: {x: 16, y: 16, width: 720, height: 688},
        stage: {x: 752, y: 16, width: 480, height: 466},
        sprites: {x: 752, y: 498, width: 480, height: 206}
    },
    showComingSoon: false,
    stageSizeMode: STAGE_SIZE_MODES.large
};

const mapStateToProps = state => ({
    // This is the button's mode, as opposed to the actual current state
    blocksId: state.bitByteGui.timeTravel.year.toString(),
    aiSidebarVisible: state.bitByteGui.modals.aiSidebar,
    floatingPanels: state.bitByteGui.floatingPanels,
    isFloatingMode: state.bitByteGui.mode.isFloatingMode,
    stageSizeMode: state.bitByteGui.stageSize.stageSize,
    theme: state.bitByteGui.theme.theme
});

const mapDispatchToProps = dispatch => ({
    onChangeFloatingMode: isFloatingMode => dispatch(setFloatingMode(isFloatingMode)),
    onMoveFloatingPanel: (panelId, x, y) => dispatch(moveFloatingPanel(panelId, x, y)),
    onResizeFloatingPanel: (panelId, width, height) => dispatch(resizeFloatingPanel(panelId, width, height)),
    onRequestCloseAISidebar: () => dispatch(closeAISidebar())
});

export default injectIntl(connect(
    mapStateToProps,
    mapDispatchToProps
)(GUIComponent));
